import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ProjectCommitRecord, VersionWorkspaceMode } from '@/services/version-workspace';

/**
 * 版本工作区前端状态 Store。
 *
 * 该 Store 只负责 UI 层状态，不请求版本接口，也不直接操作 Viewer，避免把历史预览流程和 CAD 核心生命周期耦合。
 */
export const useVersionWorkspaceStore = defineStore('version-workspace', () => {
  const mode = ref<VersionWorkspaceMode>('editing');
  const commitRecords = ref<ProjectCommitRecord[]>([]);
  const selectedCommitId = ref<string | null>(null);
  const switchingCommitId = ref<string | null>(null);
  const isRecordsLoading = ref(false);
  const isVersionSwitching = ref(false);
  const isCreatingWorkspace = ref(false);
  const recordsError = ref<string | null>(null);

  /**
   * 历史预览态只允许查看历史提交，不允许编辑当前 CAD 工作区。
   */
  const isReadonlyPreview = computed(() => mode.value === 'history-preview');

  /**
   * 当前选中的提交记录。
   */
  const selectedCommitRecord = computed(() => {
    if (!selectedCommitId.value) return undefined;

    return commitRecords.value.find((record) => record.id === selectedCommitId.value);
  });

  /**
   * 复制提交记录，避免外部数组或 parentIds 后续被修改时污染 store 状态。
   *
   * @param record 外部传入的提交记录。
   * @returns 独立的提交记录副本。
   */
  function cloneCommitRecord(record: ProjectCommitRecord): ProjectCommitRecord {
    return {
      ...record,
      parentIds: [...record.parentIds],
    };
  }

  /**
   * 设置提交记录列表。
   *
   * @param records 最新提交记录列表。
   */
  function setCommitRecords(records: readonly ProjectCommitRecord[]): void {
    commitRecords.value = records.map(cloneCommitRecord);
  }

  /**
   * 将指定提交标记为当前可编辑工作区所在提交。
   *
   * 设计说明：继续编辑历史版本时模型已经由 OpenProject 切到目标 commit，
   * 但提交记录列表不会重新请求。这里集中重算静态标签，避免 UI 仍把旧 head 显示为“当前编辑”。
   *
   * @param commitId 当前可编辑工作区对应的提交 ID。
   */
  function markCurrentWorkspaceCommit(commitId: string): void {
    const normalizedCommitId = commitId.trim();
    if (!normalizedCommitId) {
      return;
    }

    commitRecords.value = commitRecords.value.map((record) => ({
      ...record,
      parentIds: [...record.parentIds],
      isCurrentWorkspaceCommit: record.id === normalizedCommitId,
    }));
  }

  /**
   * 进入历史提交预览态。
   *
   * @param commitId 要预览的历史提交 ID。
   */
  function enterHistoryPreview(commitId: string): void {
    selectedCommitId.value = commitId;
    mode.value = 'history-preview';
  }

  /**
   * 返回可编辑工作区状态。
   */
  function enterEditingMode(): void {
    mode.value = 'editing';
    selectedCommitId.value = null;
  }

  /**
   * 标记当前正在执行模型重载的目标提交。
   *
   * @param commitId 正在下载、OpenProject 并落地到 Viewer 的提交 ID。
   */
  function beginSwitchingCommit(commitId: string): void {
    switchingCommitId.value = commitId;
    isVersionSwitching.value = true;
  }

  /**
   * 结束当前提交切换状态。
   *
   * 设计说明：历史提交切换采用 takeLatest，旧请求晚到时不能清掉新请求的行级 loading。
   *
   * @param commitId 发起结束的提交 ID；传入后只清理仍归属于该提交的 loading。
   */
  function finishSwitchingCommit(commitId?: string): void {
    if (!commitId || switchingCommitId.value === commitId) {
      switchingCommitId.value = null;
      isVersionSwitching.value = false;
    }
  }

  /**
   * 重置版本工作区状态。
   */
  function reset(): void {
    mode.value = 'editing';
    commitRecords.value = [];
    selectedCommitId.value = null;
    switchingCommitId.value = null;
    isRecordsLoading.value = false;
    isVersionSwitching.value = false;
    isCreatingWorkspace.value = false;
    recordsError.value = null;
  }

  return {
    mode,
    commitRecords,
    selectedCommitId,
    switchingCommitId,
    isRecordsLoading,
    isVersionSwitching,
    isCreatingWorkspace,
    recordsError,
    isReadonlyPreview,
    selectedCommitRecord,
    setCommitRecords,
    markCurrentWorkspaceCommit,
    enterHistoryPreview,
    enterEditingMode,
    beginSwitchingCommit,
    finishSwitchingCommit,
    reset,
  };
});
